/**
 *
 * @module adEngineClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/playback-session.md#adengineclient  // eslint-disable-line
 * @see https://wiki.bamtechmedia.com/pages/viewpage.action?pageId=17892211
 *
 */

import Check from 'check-types';
import queryString from 'query-string';

import Util from './../../util/entry';
import Exception from './../../exception/entry';
import Logging from './../../../logging/entry';
import Token from './../../token/entry';
import HttpClient from './../../platforms/httpClient';
import DustLogUtility from './../../internal/dust/dustLogUtility';
import DustUrnReference from './../../internal/dust/dustUrnReference';
import Configuration from './../../configuration/entry';

import AdEngineClientConfiguration from './adEngineClientConfiguration';
import AdEngineClientEndpoint from './adEngineClientEndpoint';

const AccessToken = Token.AccessToken;
const AdEngineClientDustUrnReference = DustUrnReference.services.media.adEngine.adEngineClient;
const HttpMethod = Configuration.HttpMethod;

const ServiceException = Exception.ServiceException;
const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class AdEngineClient
 * @since 3.6.0
 *
 */
export default class AdEngineClient {

    /**
     *
     * @param {SDK.Services.Media.adEngineClientConfiguration} adEngineClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(adEngineClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'AdEngineClient(adEngineClientConfiguration, logger, httpClient)',
            get adEngineClientConfiguration() {
                return `${this.ctor} needs SDK.Services.Media.AdEngine.AdEngineClientConfiguration instance`;
            }
        };

        instanceStrict(
            adEngineClientConfiguration,
            AdEngineClientConfiguration,
            errorMessage.adEngineClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {SDK.Services.Media.AdEngine.AdEngineClientConfiguration}
         *
         */
        this.config = adEngineClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(AdEngineClient.name, 'Created.');


    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {Object} adEngineObject
     * @returns {Promise<Void>}
     *
     */
    updateCookies(accessToken, adEngineObject) {

        const errorMessage = {
            method: 'AdEngineClient.updateCookies(namespace)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            },
            get adEngineObject() {
                return `${this.method} adEngineObject should be a non-empty Object`;
            }
        };

        nonEmptyObject(adEngineObject, errorMessage.adEngineObject, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const credentials = 'include';

        const payload = this.getPayload(
            accessToken,
            AdEngineClientEndpoint.setToken,
            adEngineObject,
            credentials
        );

        logger.info(AdEngineClient.name, `Attempting to update AdEngine cookies with: "${adEngineObject}".`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AdEngineClient.name,
            urn: AdEngineClientDustUrnReference.updateCookies,
            payload,
            method: HttpMethod.GET,
            data: {
                query: payload.url.split('?')[1]
            }
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {Object} adEngineObject
     * @returns {Promise<Cookie[]>}
     *
     */
    getCookies(accessToken, adEngineObject) {

        const errorMessage = {
            method: 'AdEngineClient.getCookies(namespace)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            },
            get query() {
                return `${this.method} adEngineObject should be a non-empty Object`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);
        nonEmptyObject(adEngineObject, errorMessage.adEngineObject, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;

        const payload = this.getPayload(
            accessToken,
            AdEngineClientEndpoint.setToken, // uses the same endpoint as updateCookies
            adEngineObject
        );

        logger.info(AdEngineClient.name, `Attempting to update AdEngine cookies with: "${adEngineObject}".`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: AdEngineClient.name,
            urn: AdEngineClientDustUrnReference.getCookies,
            payload,
            method: HttpMethod.GET,
            data: {
                query: payload.url.split('?')[1]
            }
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });
    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {AdEngineClientEndpoint} rel - endpoint type
     * @param {Object} adEngineObject
     * @param {String} credentials
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, adEngineObject, credentials) {

        const { endpoints } = this.config;
        const { href, headers } = endpoints[rel];

        const requestHeaders = Object.assign({}, headers);
        const url = `${href}?${queryString.stringify(adEngineObject)}`;

        if (Check.assigned(requestHeaders.Authorization)) {
            requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, accessToken.token);
        }

        return {
            url,
            headers: requestHeaders,
            credentials
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/media/adEngine/adEngineClient.js